import { HStack, VStack } from "native-base";
import { Routes, Route, useNavigate, Navigate, useLocation } from "react-router-dom";
import TopBar from "atoms/TopBar";
import routeData, { sdClassicRoute, sdAgentBothRoute } from "global/routes";
import { getStore } from "store/storeUtils";
import jwtDecode from "jwt-decode";
import React, { Suspense, useEffect } from "react";
import secureStore from "utils/secureStore";
import AmplitudeHelper from "utils/analytics";
import { SentryHelper } from "utils/performance/SentryHelper";
import { fetchMeApiCall, getEmployerConfig } from "api/useQueryCalls";
import ErrorFallBack from "pages/ErrorFallBack";
import useOnlineStatus from "utils/useOnlineStatus";
import ShowOnlineBanner from "atoms/ShowOnlineBanner";
import ModalAlerts from "atoms/ModalAlerts";
import { MODAL_ALERTS } from "global/constants";
import { canShowAccessAlert, canShowSuspendedAlert, forceLogoutOnWAS, getIfSDA, getSdFlowMode, SD_FLOW_MODES } from "utils/utilities";
import { AMPLITUDE_EVENTSOURCES } from "utils/analytics/constants";
import { showComponents } from "pages/SingleCreate/utils";
import { IOB, TEAM, NOTIFICATIONS, SD_AGENT_PAGE, SD_LANDING_PAGE } from "global/paths";
import moengage from "@moengage/web-sdk";

const NavBar = React.lazy(() => import("molecules/NavBar"));
const KYBDashboard = React.lazy(() => import("pages/KYB/KYBDashboard"));
const Notifications = React.lazy(() => import("pages/Notifications"));

export function modalAlertHandler(
  isSuccess,
  data,
  setModalAlert,
  setMeApiData,
  resetLoginData,
  navigate,
  employerConfigData,
) {
  if (isSuccess) {
    if (canShowSuspendedAlert(data, employerConfigData, false)) {
      setModalAlert({ ...MODAL_ALERTS.WS_MODAL, source: AMPLITUDE_EVENTSOURCES.team_page });
      secureStore.setItemAsync("hasViewedWS", true);
    }
    setMeApiData(data);
    forceLogoutOnWAS(data, resetLoginData, navigate);
  }
}

export function shouldRenderLandingRoute(employerConfigData) {
  if (employerConfigData?.isInitialOnboarding) return <Route path="/" element={<Navigate to={IOB} replace />} />;
  return <Route path="/iob" element={<Navigate to={TEAM} replace />} />;
}

export function getBaseRouteForNotFound(employerConfigData) {
  if (employerConfigData?.isInitialOnboarding === true) return IOB;
  const firstRoute = routeData.filter((item) => employerConfigData?.menu?.[0] === item.accessKey);
  return firstRoute[0]?.path;
}

export async function iobFirstTimeModalHandler(employerConfigData, setModalAlert) {
  if (employerConfigData?.isKYBEnabled) return;
  if (employerConfigData && employerConfigData?.isInitialOnboarding && employerConfigData?.isFirstLogin) {
    const hasViewedFirstLogin = await secureStore.getItemAsync("hasViewedFirstLogin");
    if (!hasViewedFirstLogin) {
      setModalAlert({ ...MODAL_ALERTS.INITIAL_ONBOARDING, source: AMPLITUDE_EVENTSOURCES.team_page });
      await secureStore.setItemAsync("hasViewedFirstLogin", true);
    }
  }
  if (employerConfigData?.isEndOfInitialOnboarding === true) {
    setModalAlert({ ...MODAL_ALERTS.IOB_CONGRATULATIONS, source: AMPLITUDE_EVENTSOURCES.team_page });
    await secureStore.setItemAsync("hasViewedEndIOBModal", true);
    await secureStore.setItemAsync("hasViewedWS", true);
  }
}

function renderRoute(route) {
  if (route.children) {
    return (
      <Route key={`${route?.id}-${route?.path}`} path={route?.path} element={route?.element}>
        {route?.children?.map((child) => (
          <Route key={`${child?.id}-${child?.path}`} path={child?.path} element={child?.element} />
        ))}
      </Route>
    );
  }
  return <Route key={`${route?.id}-${route?.path}`} path={route?.path} element={route?.element} />;
}

export function shouldRenderRoutes(employerConfigData, isSda) {
  const sdFlowMode = getSdFlowMode(employerConfigData);
  return routeData.map((route) => {
    if (!employerConfigData) return null;
    if (!isSda && route?.isSDA) return null;
    if (!showComponents(employerConfigData, route)) return null;
    if (!employerConfigData?.isInitialOnboarding) {
      const menusAccessible = employerConfigData.menu?.indexOf(route.accessKey);
      if (menusAccessible < 0) return null;
    }

    if (!route.isSDA) return renderRoute(route);

    // SD experience per derived mode: classic at /sd, agent at /sd (NEW_ONLY) or
    // as a sibling at /sd/agent (BOTH, reached via the promo banner)
    if (sdFlowMode === SD_FLOW_MODES.NEW_ONLY) return renderRoute(route);
    if (sdFlowMode === SD_FLOW_MODES.BOTH) {
      return (
        <React.Fragment key={`${route?.id}-sd-both`}>
          {renderRoute(sdClassicRoute)}
          {renderRoute(sdAgentBothRoute)}
        </React.Fragment>
      );
    }
    return renderRoute(sdClassicRoute);
  });
}

const Home = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userData, setMeApiData, setModalAlert, resetLoginData, setEmployerDataConfig, employerConfigData } =
    getStore();

  const { data, isSuccess } = fetchMeApiCall();
  const { data: employerConfigApiData, isSuccess: isConfigDataSuccess } = getEmployerConfig();

  useEffect(() => {
    modalAlertHandler(isSuccess, data, setModalAlert, setMeApiData, resetLoginData, navigate, employerConfigApiData);
  }, [data, isSuccess, employerConfigApiData]);

  useEffect(() => {
    if (canShowAccessAlert(employerConfigData, location)) {
      setModalAlert({ ...MODAL_ALERTS.ACCESS_DENIED });
    }
  }, [employerConfigApiData, location]);

  useEffect(() => {
    iobFirstTimeModalHandler(employerConfigApiData, setModalAlert);
  }, [employerConfigApiData, isConfigDataSuccess]);

  useEffect(() => {
    (() => {
      const token = secureStore.getItemAsync("accessToken");
      const decodedToken = jwtDecode(token);
      moengage.add_first_name(userData.userName);
      moengage.add_unique_user_id(decodedToken.userId);
      moengage.add_user_attribute("user_id", decodedToken.userId);
      moengage.add_user_attribute("employer_id", decodedToken.employerId);
      moengage.add_user_attribute("full_name", userData.userName);

      AmplitudeHelper.setUserProperties({
        userId: decodedToken.userId,
        employerId: decodedToken.employerId,
        name: userData.userName,
      });
      SentryHelper.setUser(decodedToken.userId, decodedToken.employerId, userData.userName);
    })();
  }, []);

  useEffect(() => {
    if (isConfigDataSuccess) setEmployerDataConfig(employerConfigApiData);
  }, [isConfigDataSuccess, employerConfigApiData]);

  const isSda = getIfSDA(employerConfigData);
  const isNotificationsEnabled = Boolean(
    employerConfigData?.isNotificationsEnabled ?? employerConfigData?.isNotifcationsEnabled,
  );

  const isOnline = useOnlineStatus();

  const refreshPage = () => {
    navigate("/");
  };

  return (
    <VStack>
      <TopBar name={userData?.userName ? userData?.userName.split(" ")[0] : ""} />
      <ModalAlerts />
      <HStack height="92vh">
        {!isOnline && <ShowOnlineBanner refreshPage={refreshPage} />}
        <Suspense fallback={<div>Loading...</div>}>
          {employerConfigData?.isKYBEnabled ? (
            <KYBDashboard />
          ) : (
            <>
              <NavBar isSda={isSda} />
              <ErrorFallBack navigate={navigate}>
                <Routes>
                  {shouldRenderRoutes(employerConfigData, isSda)}
                  {/* Bookmarked /sd/agent lands on SD (not the catch-all) when the agent isn't a sibling route */}
                  {isSda && getSdFlowMode(employerConfigData) !== SD_FLOW_MODES.BOTH && (
                    <Route path={SD_AGENT_PAGE} element={<Navigate to={SD_LANDING_PAGE} replace />} />
                  )}
                  <Route
                    path={NOTIFICATIONS}
                    element={
                      isNotificationsEnabled ? (
                        <Notifications />
                      ) : (
                        <Navigate to={getBaseRouteForNotFound(employerConfigData)} replace />
                      )
                    }
                  />
                  {shouldRenderLandingRoute(employerConfigData)}
                  <Route path="*" element={<Navigate to={getBaseRouteForNotFound(employerConfigData)} replace />} />
                </Routes>
              </ErrorFallBack>
            </>
          )}
        </Suspense>
      </HStack>
    </VStack>
  );
};

export default Home;
